import * as RToast from "@radix-ui/react-toast";
import { createContext, useCallback, useContext, useState } from "react";
import type { ReactNode } from "react";

export type ToastVariant = "info" | "success" | "error";

export interface ToastMessage {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  durationMs?: number;
}

interface ToastContextValue {
  push: (msg: Omit<ToastMessage, "id">) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === undefined) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}

export interface ToastProviderProps {
  children: ReactNode;
  swipeDirection?: "right" | "left" | "up" | "down";
}

export function ToastProvider({
  children,
  swipeDirection = "right",
}: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const push = useCallback((msg: Omit<ToastMessage, "id">): string => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, ...msg }]);
    return id;
  }, []);

  const dismiss = useCallback((id: string): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ push, dismiss }}>
      <RToast.Provider swipeDirection={swipeDirection}>
        {children}
        {toasts.map((t) => (
          <RToast.Root
            key={t.id}
            className={`ds-toast ds-toast--${t.variant ?? "info"}`}
            duration={t.durationMs ?? 5000}
            onOpenChange={(open) => {
              if (!open) dismiss(t.id);
            }}
          >
            <RToast.Title className="ds-toast__title">{t.title}</RToast.Title>
            {t.description !== undefined && (
              <RToast.Description className="ds-toast__description">
                {t.description}
              </RToast.Description>
            )}
            <RToast.Close className="ds-toast__close" aria-label="Dismiss">
              ×
            </RToast.Close>
          </RToast.Root>
        ))}
        <RToast.Viewport className="ds-toast__viewport" />
      </RToast.Provider>
    </ToastContext.Provider>
  );
}
